import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { useAuthStore } from '@/stores/useAuthStore'

type NotificationSettings = {
  /** Aviso sonoro (y vibracion en movil) cuando entra un chat a la cola. */
  queuedChatSound: boolean
  /** Notificacion de escritorio cuando entra un chat a la cola. */
  queuedChatDesktop: boolean
}

const DEFAULT_SETTINGS: NotificationSettings = {
  queuedChatSound: true,
  queuedChatDesktop: true,
}

// Por usuario: en el Oper es normal que varios agentes usen el mismo equipo por
// turnos, y la preferencia de uno no debe llegarle al siguiente.
const storageKeyFor = (userId: string | null | undefined) =>
  `calloperator:notification-settings:${userId || 'anonymous'}`

export const useNotificationSettingsStore = defineStore('notification-settings', () => {
  const authStore = useAuthStore()

  const settings = ref<NotificationSettings>({ ...DEFAULT_SETTINGS })

  const storageKey = computed(() => storageKeyFor(authStore.userId))

  const load = () => {
    // Un navegador que bloquea el almacenamiento no puede tumbar la app: se
    // trabaja con los valores por defecto y solo se pierde la preferencia.
    try {
      const stored = localStorage.getItem(storageKey.value)
      settings.value = stored
        ? { ...DEFAULT_SETTINGS, ...(JSON.parse(stored) as Partial<NotificationSettings>) }
        : { ...DEFAULT_SETTINGS }
    } catch {
      settings.value = { ...DEFAULT_SETTINGS }
    }
  }

  const persist = () => {
    try {
      localStorage.setItem(storageKey.value, JSON.stringify(settings.value))
    } catch {
      // Sin almacenamiento la preferencia dura lo que la sesion.
    }
  }

  const queuedChatSound = computed(() => settings.value.queuedChatSound)
  const queuedChatDesktop = computed(() => settings.value.queuedChatDesktop)

  /** Hay algo silenciado: lo usa la campana para no mentir sobre su estado. */
  const hasMutedNotifications = computed(
    () => !settings.value.queuedChatSound || !settings.value.queuedChatDesktop,
  )

  const setQueuedChatSound = (enabled: boolean) => {
    settings.value.queuedChatSound = enabled
    persist()
  }

  const setQueuedChatDesktop = (enabled: boolean) => {
    settings.value.queuedChatDesktop = enabled
    persist()
  }

  // Cambiar de agente en el mismo navegador tiene que traer sus preferencias.
  watch(() => authStore.userId, load)

  load()

  return {
    queuedChatSound,
    queuedChatDesktop,
    hasMutedNotifications,
    setQueuedChatSound,
    setQueuedChatDesktop,
    load,
  }
})
